import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { RootState } from "./index";
import config from "../config";
import globalTenants from "../globalTenants";
import { TenantMetadata } from "../domain/TenantSlice/TenantMetadata";
import { NaturimalMetadata } from "../tenantMatrix/naturimal/NaturimalMetadata";
import { JuncosMetadata } from "../tenantMatrix/juncos/JuncosMetadata";
import { useSelector, useDispatch } from "react-redux";
import type { AppDispatch } from "./index";

type TenantKey = string;

const themes: Record<TenantKey, TenantMetadata> = {
  [globalTenants.NATURIMAL]: NaturimalMetadata,
  [globalTenants.LOS_JUNCOS]: JuncosMetadata,
};

export interface TenantMetadataState {
  tenantKey: TenantKey;
  theme: TenantMetadata;
}

const initialTenantKey: TenantKey = config.tenant;

const initialState: TenantMetadataState = {
  tenantKey: initialTenantKey,
  theme: themes[initialTenantKey] ?? NaturimalMetadata,
};

const tenantMetadataSlice = createSlice({
  name: "tenantMetadata",
  initialState,
  reducers: {
    setTenantByKey(state, action: PayloadAction<TenantKey>) {
      const key = action.payload;
      state.tenantKey = key;
      state.theme = themes[key] ?? state.theme;
    },
    setTenantTheme(state, action: PayloadAction<TenantMetadata>) {
      state.theme = action.payload;
    },
  },
});

export const { setTenantByKey, setTenantTheme } = tenantMetadataSlice.actions;
export default tenantMetadataSlice.reducer;

// selectors
export const selectTenantKey = (s: RootState) => s.tenantMetadata.tenantKey;
export const selectTenantTheme = (s: RootState) => s.tenantMetadata.theme;

// facade hook (convention-aligned with other slices)
export function useTenantMetadataStore(): TenantMetadata {
  const theme = useSelector(selectTenantTheme);
  // expose dispatcher in case future features need switching tenants
  useDispatch<AppDispatch>();
  return theme;
}
